import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AtSign,
  Bookmark,
  ChevronDown,
  ChevronUp,
  Filter,
  Hash,
  RotateCcw,
  Search,
  Users,
  X,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import { Button } from '@/ui';
import type { Capabilities, SearchFilters } from './contracts';
import { errorCopy, networks } from './service';
import { Field, Small } from './styles';
import {
  SearchLayout,
  SearchRow,
  FilterToggle,
  FilterPanel,
  PanelHead,
  FilterForm,
  FieldGrid,
  InputWrap,
  FilterFooter,
  FilterActions,
} from './SearchTools.styles';

interface SearchToolsProps {
  query: string;
  searchField: ReactNode;
  children?: ReactNode;
  activeCategory?: boolean;
  onClear?: () => void;
}

export function SearchTools(props: SearchToolsProps) {
  const { currentUser } = useSession();
  const caps = useAsyncRead<Capabilities | null>(
    networks.capabilities,
    currentUser?.id ?? 'guest',
    null,
  );
  return (
    <SearchToolsForm
      key={currentUser?.id ?? 'guest'}
      {...props}
      signedIn={!!currentUser}
      advancedEnabled={!!caps.data?.flags?.saved_search_notification_budget}
    />
  );
}
function SearchToolsForm({
  query,
  signedIn,
  advancedEnabled,
  searchField,
  children,
  activeCategory,
  onClear,
}: SearchToolsProps & { signedIn: boolean; advancedEnabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }
  const [params, setParams] = useSearchParams();
  // Query-only navigation must not discard unfinished advanced fields. Our own
  // Apply/Clear navigation acknowledges the existing form instead of remounting
  // it a second time after the router commits its transition.
  const filterSignature = JSON.stringify(
    ['author', 'community', 'topic', 'from', 'until', 'media'].map((key) => params.get(key)),
  );
  const [seenFilters, setSeenFilters] = useState(filterSignature);
  const [expectedFilters, setExpectedFilters] = useState<string | null>(null);
  if (seenFilters !== filterSignature) {
    setSeenFilters(filterSignature);
    if (expectedFilters !== filterSignature) setResetKey((value) => value + 1);
    setExpectedFilters(null);
  }
  function navigateFilters(next: URLSearchParams) {
    setExpectedFilters(
      JSON.stringify(
        ['author', 'community', 'topic', 'from', 'until', 'media'].map((key) => next.get(key)),
      ),
    );
    setParams(next);
  }
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  function filters(): SearchFilters {
    const data = new FormData(formRef.current!);
    return {
      query: query.trim(),
      author: String(data.get('author')),
      community: String(data.get('community')),
      topic: String(data.get('topic')),
      from: String(data.get('from')),
      until: String(data.get('until')),
      media: String(data.get('media')) as SearchFilters['media'],
    };
  }
  const activeCount =
    ['author', 'community', 'topic', 'from', 'until', 'media'].filter(
      (key) => params.get(key) && params.get(key) !== 'any',
    ).length + Number(!!activeCategory);
  return (
    <SearchLayout
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          close();
        }
      }}
    >
      <SearchRow>
        {searchField}
        <FilterToggle
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          <Filter size={17} aria-hidden="true" />
          Filters{activeCount > 0 && <span>{activeCount}</span>}
          {open ? (
            <ChevronUp size={17} aria-hidden="true" />
          ) : (
            <ChevronDown size={17} aria-hidden="true" />
          )}
        </FilterToggle>
      </SearchRow>
      <FilterPanel id={panelId} hidden={!open} role="region" aria-labelledby={`${panelId}-title`}>
        <PanelHead>
          <div>
            <h2 id={`${panelId}-title`}>Search filters</h2>
            <Small>Refine your search to find exactly what you need.</Small>
          </div>
          <Button type="button" variant="ghost" aria-label="Close search filters" onClick={close}>
            <X size={18} aria-hidden="true" />
          </Button>
        </PanelHead>
        {children}
        {advancedEnabled && (
          <FilterForm
            ref={formRef}
            key={resetKey}
            onSubmit={(e) => {
              e.preventDefault();
              const values = filters();
              const next = new URLSearchParams();
              for (const [key, value] of Object.entries(values))
                if (value && value !== 'any') next.set(key === 'query' ? 'q' : key, value);
              navigateFilters(next);
            }}
          >
            <FieldGrid>
              {(['author', 'community', 'topic', 'from', 'until'] as const).map((key) => (
                <Field key={key}>
                  {
                    {
                      author: 'Author handle',
                      community: 'Community slug',
                      topic: 'Topic',
                      from: 'From date',
                      until: 'Until date',
                    }[key]
                  }
                  <InputWrap>
                    {key === 'author' && <AtSign size={17} aria-hidden="true" />}
                    {key === 'community' && <Users size={17} aria-hidden="true" />}
                    {key === 'topic' && <Hash size={17} aria-hidden="true" />}
                    <input
                      name={key}
                      placeholder={
                        key === 'author'
                          ? 'e.g. truxsocial'
                          : key === 'community'
                            ? 'e.g. web3'
                            : key === 'topic'
                              ? 'e.g. research'
                              : undefined
                      }
                      type={key === 'from' || key === 'until' ? 'date' : 'text'}
                      defaultValue={params.get(key) ?? ''}
                      maxLength={80}
                    />
                  </InputWrap>
                </Field>
              ))}
              <Field>
                Media
                <select name="media" aria-label="Media" defaultValue={params.get('media') ?? 'any'}>
                  <option value="any">Any</option>
                  <option value="image">Image</option>
                  <option value="video">External video link</option>
                  <option value="link">Link</option>
                </select>
              </Field>
            </FieldGrid>
            {signedIn && (
              <>
                <Field>
                  Saved search name
                  <InputWrap>
                    <Bookmark size={17} aria-hidden="true" />
                    <input name="title" maxLength={120} placeholder="My useful search" />
                  </InputWrap>
                </Field>
              </>
            )}
            <FilterFooter>
              <FilterActions>
                <Button type="submit">
                  <Search size={16} aria-hidden="true" />
                  Apply filters
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    // Clear current values immediately; do not seed the new
                    // form from the old URL while navigation is pending.
                    if (formRef.current) {
                      for (const element of Array.from(formRef.current.elements)) {
                        if (element instanceof HTMLInputElement) element.value = '';
                        if (element instanceof HTMLSelectElement) element.value = 'any';
                      }
                    }
                    navigateFilters(new URLSearchParams(query ? { q: query } : {}));
                    onClear?.();
                  }}
                >
                  <RotateCcw size={16} aria-hidden="true" />
                  Clear filters
                </Button>
                {signedIn && (
                  <Button
                    variant="secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (busy) return;
                      const values = filters();
                      const data = new FormData(formRef.current!);
                      setBusy(true);
                      setError('');
                      void networks
                        .create('saved-searches', {
                          title:
                            String(data.get('title')).trim() || query.trim() || 'My saved search',
                          description: '',
                          visibility: 'private',
                          data: { filters: values, cadence: 'none' },
                        })
                        .then(
                          (r) => {
                            if (alive.current)
                              navigate(`/saved-searches/${encodeURIComponent(r.slug)}`);
                          },
                          (e) => {
                            if (alive.current) setError(errorCopy(e));
                          },
                        )
                        .finally(() => {
                          if (alive.current) setBusy(false);
                        });
                    }}
                  >
                    <Bookmark size={16} aria-hidden="true" />
                    Save this search
                  </Button>
                )}
              </FilterActions>
              {signedIn && (
                <Small>
                  Saved privately. Alerts start off; choose a cadence in the saved search.
                </Small>
              )}
            </FilterFooter>
            {error && <p role="alert">{error}</p>}
          </FilterForm>
        )}
      </FilterPanel>
    </SearchLayout>
  );
}
